'use client';

import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useForm, type ControllerRenderProps, type FieldErrors } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { PhoneInput } from '@/components/ui/phone-input';
import { FormRichTextField } from '@/components/forms/form-rich-text-field';
import { useUnsavedChangesGuard } from '@/hooks/use-unsaved-changes-guard';
import {
  Loader2,
  Upload,
  Sparkles,
  Building2,
  MapPin,
  Phone,
  Mail,
  Globe,
  Link2,
  FileText,
  Tag,
  Layers,
  Workflow,
  Camera,
  Search,
  TriangleAlert,
  Share2,
} from 'lucide-react';
import axiosInstance from '@/lib/axios';
import { prepareImageFile } from '@/lib/prepare-image-file';
import { toast } from 'sonner';
import * as Sentry from '@sentry/react';
import { useUser } from '@/hooks/use-user';
import { useSWRConfig } from 'swr';
import type { Business, BusinessAddress } from '@/types';
import { ImageUpload } from '@/components/ui/image-upload';
import { BusinessCategoryPicker } from '@/components/common/business-category-picker';
import {
  parseBusinessCategory,
  composeBusinessCategory,
  formatBusinessCategoryLabel,
} from '@/lib/business-category';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAddressAutofill } from '@/hooks/use-address-autofill';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { richHtmlIsEmpty } from '@/lib/strip-html';
import { SocialLinksEditor } from '@/components/social/social-links-editor';

const addressSchema = z.object({
  addressLine1: z.string().min(2, 'Address line 1 is required'),
  addressLine2: z.string().optional(),
  landmark: z.string().optional(),
  taluka: z.string().optional(),
  district: z.string().optional(),
  state: z.string().min(2, 'State is required'),
  country: z.string().min(2, 'Country is required'),
  pinCode: z.string().min(4, 'Pin code is invalid'),
  googleMapLink: z.string().url('Invalid URL').optional().or(z.literal('')),
});

const richTextField = z.string().max(65535).optional();

const businessFormSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  businessCategory: z.string().trim().min(1, 'Category is required'),
  subCategory: z.string().trim().min(1, 'Sub category is required'),
  selfDefinedCategory: z.string().trim().max(255).optional().or(z.literal('')),
  description: richTextField,
  speciality: richTextField,
  addressJson: addressSchema,
  phone: z.string().min(10, 'Phone number is invalid'),
  email: z.string().email('Invalid email address'),
  logo: z.string().optional(),
  website: z.string().url('Invalid URL').optional().or(z.literal('')),
  websiteInfo: z.string().optional(),
  keywords: z.string().optional(),
  workArea: z.enum(['Global', 'National Level', 'State Level', 'Local']).optional(),
  priority: z.enum(['PRIMARY', 'SECONDARY', 'OTHER']).optional(),
  socialLinks: z.record(z.string(), z.string()).optional(),
  id: z.string().optional(),
});

type BusinessFormValues = z.infer<typeof businessFormSchema>;

interface BusinessFormProps {
  initialData?: Business;
  /** When creating a business from a community URL, links to that community (see POST /api/user/business). */
  linkToCommunityId?: string;
  onSuccess?: (business: Pick<Business, 'id'>) => void;
}

// Helper function to parse legacy address string
const parseLegacyAddress = (address: string | BusinessAddress): BusinessAddress => {
  if (!address) {
    return {
      addressLine1: '',
      addressLine2: '',
      landmark: '',
      taluka: '',
      district: '',
      state: '',
      country: '',
      pinCode: '',
      googleMapLink: '',
    };
  }

  if (typeof address !== 'string') {
    return {
      ...parseLegacyAddress(''),
      ...address,
    };
  }

  // Try to parse JSON string
  try {
    const parsed = JSON.parse(address);
    return {
      addressLine1: parsed.addressLine1 || '',
      addressLine2: parsed.addressLine2 || '',
      landmark: parsed.landmark || '',
      taluka: parsed.taluka || '',
      district: parsed.district || '',
      state: parsed.state || '',
      country: parsed.country || '',
      pinCode: parsed.pinCode || '',
      googleMapLink: parsed.googleMapLink || '',
    };
  } catch {
    // Return empty structure for legacy string addresses
    return {
      addressLine1: '',
      addressLine2: '',
      landmark: '',
      taluka: '',
      district: '',
      state: '',
      country: '',
      pinCode: '',
      googleMapLink: '',
    };
  }
};

function mergeBusinessAddress(initialData?: Business): BusinessAddress {
  const skeleton = parseLegacyAddress('');
  if (!initialData) return skeleton;

  const fromLegacy = initialData.address ? parseLegacyAddress(initialData.address) : skeleton;
  const fromJsonObj =
    initialData.addressJson && typeof initialData.addressJson === 'object'
      ? (initialData.addressJson as Partial<BusinessAddress>)
      : {};

  const keys: (keyof BusinessAddress)[] = [
    'addressLine1',
    'addressLine2',
    'landmark',
    'taluka',
    'district',
    'state',
    'country',
    'pinCode',
    'googleMapLink',
  ];
  const merged: BusinessAddress = { ...skeleton };

  for (const k of keys) {
    const jv = fromJsonObj[k];
    const hasJson = jv != null && (typeof jv !== 'string' || (jv as string).trim().length > 0);

    if (hasJson) {
      merged[k] = typeof jv === 'string' ? jv : String(jv);
    } else {
      const fallback = fromLegacy[k];
      merged[k] =
        fallback == null ? '' : typeof fallback === 'string' ? fallback : String(fallback);
    }
  }

  return merged;
}

export function BusinessForm({ initialData, linkToCommunityId, onSuccess }: BusinessFormProps) {
  const { user, mutate } = useUser();
  const { mutate: globalMutate } = useSWRConfig();
  const { fetchAddress } = useAddressAutofill();
  const [isLoading, setIsLoading] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [isGeneratingKeywords, setIsGeneratingKeywords] = useState(false);
  const [pincodeLoading, setPincodeLoading] = useState(false);
  const [addressOptions, setAddressOptions] = useState<{
    states: string[];
    districts: string[];
    talukas: string[];
  }>({ states: [], districts: [], talukas: [] });
  const [similarBusinesses, setSimilarBusinesses] = useState<Business[]>([]);
  const [showDuplicatePopup, setShowDuplicatePopup] = useState(false);
  const formContainerRef = useRef<HTMLDivElement>(null);
  const [openAccordions, setOpenAccordions] = useState<string[]>(['basic']);
  const [refineTarget, setRefineTarget] = useState<'description' | 'speciality' | null>(null);
  const [refineDraft, setRefineDraft] = useState('');
  const [refiningField, setRefiningField] = useState<null | 'description' | 'speciality'>(null);
  const [allowLeave, setAllowLeave] = useState(false);

  const parsedInitialCategory =
    initialData?.subCategory != null
      ? {
          businessCategory: initialData?.category || '',
          subCategory: initialData.subCategory || '',
        }
      : parseBusinessCategory(initialData?.category);

  const form = useForm<BusinessFormValues>({
    resolver: zodResolver(businessFormSchema),
    defaultValues: {
      id: initialData?.id || '',
      name: initialData?.name || '',
      businessCategory:
        initialData?.businessCategory || parsedInitialCategory.businessCategory || '',
      subCategory: initialData?.subCategory || parsedInitialCategory.subCategory || '',
      selfDefinedCategory: initialData?.selfDefinedCategory || '',
      description: initialData?.description || '',
      speciality: initialData?.speciality || '',
      addressJson: mergeBusinessAddress(initialData),
      phone: initialData?.phone
        ? initialData.phone.startsWith('+')
          ? initialData.phone
          : `+${initialData.phone}`
        : user?.phone
          ? user.phone.startsWith('+')
            ? user.phone
            : `+${user.phone}`
          : '',
      email: initialData?.email || user?.email || '',
      logo: initialData?.logo || '',
      website: initialData?.website || '',
      websiteInfo: initialData?.websiteInfo || '',
      keywords: initialData?.keywords || '',
      workArea: initialData?.workArea as
        | 'Global'
        | 'National Level'
        | 'State Level'
        | 'Local'
        | undefined,
      priority: (initialData?.priority as 'PRIMARY' | 'SECONDARY' | 'OTHER' | undefined) ?? 'OTHER',
      socialLinks: initialData?.socialLinks ?? {},
    },
  });

  // Watch pincode field for changes
  const pincodeValue = form.watch('addressJson.pinCode');
  const watchedBusinessCategory = form.watch('businessCategory');
  const watchedSubCategory = form.watch('subCategory');
  const watchedSelfDefinedCategory = form.watch('selfDefinedCategory');
  const { isDirty } = form.formState;
  const { dialog: unsavedChangesDialog } = useUnsavedChangesGuard({
    when: isDirty && !isLoading && !allowLeave,
    title: 'Leave without saving?',
    description: 'You have unsaved business details. If you leave now, your progress will be lost.',
    confirmLabel: 'Leave',
    cancelLabel: 'Stay',
  });

  // Fetch address options when editing existing business with pincode
  useEffect(() => {
    if (initialData?.addressJson || initialData?.address) {
      const addr = (initialData.addressJson || initialData.address) as unknown as BusinessAddress;
      if (addr.pinCode) {
        fetchAddress(addr.pinCode).then((data) => {
          if (data) {
            setAddressOptions({
              states: data.states ?? [],
              districts: data.districts ?? [],
              talukas: data.talukas ?? [],
            });
          }
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Watch business name for duplicates
  const businessName = form.watch('name');

  useEffect(() => {
    // Only check for duplicates if it's a new business (no initialData)
    // and the name is at least 2 characters long
    console.log('Business Name changed:', businessName, 'Has initialData:', !!initialData);
    if (initialData || !businessName || businessName.length < 2) {
      setSimilarBusinesses([]);
      return;
    }

    const timer = setTimeout(async () => {
      console.log('Searching for duplicates for:', businessName);
      try {
        const response = await axiosInstance.get(
          `/api/user/business/search?name=${encodeURIComponent(businessName)}`
        );
        console.log('Search results:', response.data);
        if (response.data && response.data.length > 0) {
          setSimilarBusinesses(response.data);
          setShowDuplicatePopup(true);
          console.log('Showing duplicate popup');
        } else {
          setSimilarBusinesses([]);
        }
      } catch (error) {
        console.error('Duplicate check error:', error);
      }
    }, 800);

    return () => clearTimeout(timer);
  }, [businessName, initialData]);

  const handleJoin = async (businessId: string) => {
    console.log('Joining business:', businessId);
    setIsLoading(true);
    try {
      const response = await axiosInstance.post('/api/user/business/join', { businessId });
      console.log('Join response:', response.data);
      await mutate();
      toast.success('Joined business successfully!');
      setShowDuplicatePopup(false);
    } catch (error) {
      console.error('Join error:', error);
      toast.error('Failed to join business');
    } finally {
      setIsLoading(false);
    }
  };

  // Effect to trigger autofill when pincode changes
  useEffect(() => {
    if (pincodeValue && pincodeValue.length >= 5) {
      const timeoutId = setTimeout(() => {
        handlePincodeChange(pincodeValue);
      }, 500); // Debounce by 500ms
      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pincodeValue]);

  // Handle pincode change for autofill
  const handlePincodeChange = async (pincode: string | undefined) => {
    if (!pincode || pincode.length < 5) return;
    setPincodeLoading(true);
    try {
      const addressData = await fetchAddress(pincode);
      if (addressData) {
        setAddressOptions({
          states: addressData.states ?? [],
          districts: addressData.districts ?? [],
          talukas: addressData.talukas ?? [],
        });

        form.setValue('addressJson.country', addressData.country);
        form.setValue('addressJson.state', addressData.state);
        form.setValue('addressJson.district', addressData.district);
        form.setValue('addressJson.taluka', addressData.taluka);
      }
    } finally {
      setPincodeLoading(false);
    }
  };

  const closeRefineDialog = () => {
    setRefineTarget(null);
    setRefineDraft('');
  };

  const openGenerateDialog = (target: 'description' | 'speciality') => {
    setRefineTarget(target);
    setRefineDraft('');
  };

  const buildRefineBusinessContext = () => {
    const v = form.getValues();
    const aj = v.addressJson;
    const locParts = [aj?.district, aj?.taluka, aj?.state, aj?.country]
      .map((x) => (typeof x === 'string' ? x.trim() : ''))
      .filter(Boolean);
    const locality =
      locParts.length > 0 ? [...new Set(locParts)].slice(0, 5).join(', ') : undefined;

    const ctx: {
      name?: string;
      category?: string;
      workArea?: string;
      keywords?: string;
      website?: string;
      websiteInfo?: string;
      locality?: string;
    } = {};
    if (v.name?.trim()) ctx.name = v.name.trim();
    const categoryLabel =
      v.businessCategory?.trim() && v.subCategory?.trim()
        ? composeBusinessCategory(v.businessCategory, v.subCategory)
        : v.subCategory?.trim() || v.businessCategory?.trim();
    if (categoryLabel) ctx.category = categoryLabel;
    if (v.workArea) ctx.workArea = v.workArea;
    if (v.keywords?.trim()) ctx.keywords = v.keywords.trim();
    if (v.website?.trim()) ctx.website = v.website.trim();
    if (v.websiteInfo?.trim()) ctx.websiteInfo = v.websiteInfo.trim();
    if (locality) ctx.locality = locality;

    return Object.keys(ctx).length > 0 ? ctx : undefined;
  };

  const runEnhance = async (target: 'description' | 'speciality') => {
    const currentHtml = form.getValues(target);
    if (richHtmlIsEmpty(currentHtml)) {
      toast.error('Add text first, or use Generate.');
      return;
    }
    setRefiningField(target);
    try {
      const { data } = await axiosInstance.post<{ html: string }>(
        '/api/user/business/refine-rich-field',
        {
          field: target === 'description' ? 'about' : 'speciality',
          mode: 'enhance',
          currentHtml,
          businessContext: buildRefineBusinessContext(),
        }
      );
      if (data.html) {
        form.setValue(target, data.html);
        toast.success(target === 'description' ? 'About us enhanced' : 'Speciality enhanced');
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const body = error.response?.data as { error?: string } | undefined;
        toast.error(body?.error || 'Could not refine text');
      } else {
        toast.error('Could not refine text');
      }
    } finally {
      setRefiningField(null);
    }
  };

  const submitGenerateFromDialog = async () => {
    if (!refineTarget) return;
    const hint = refineDraft.trim();
    if (hint.length < 3) {
      toast.error('Please share at least a few words.');
      return;
    }
    setRefiningField(refineTarget);
    try {
      const { data } = await axiosInstance.post<{ html: string }>(
        '/api/user/business/refine-rich-field',
        {
          field: refineTarget === 'description' ? 'about' : 'speciality',
          mode: 'generate',
          userHint: hint,
          businessContext: buildRefineBusinessContext(),
        }
      );
      if (data.html) {
        form.setValue(refineTarget, data.html);
        closeRefineDialog();
        toast.success(
          refineTarget === 'description' ? 'About us generated' : 'Speciality generated'
        );
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const body = error.response?.data as { error?: string } | undefined;
        toast.error(body?.error || 'Could not refine text');
      } else {
        toast.error('Could not refine text');
      }
    } finally {
      setRefiningField(null);
    }
  };

  const renderRichAssistActions = (
    target: 'description' | 'speciality',
    field: ControllerRenderProps<BusinessFormValues, 'description' | 'speciality'>
  ) => {
    const empty = richHtmlIsEmpty(field.value as string);
    const busy = refiningField === target;
    return (
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-8 gap-1.5 shrink-0 text-xs font-semibold"
        disabled={busy}
        onClick={() => (empty ? openGenerateDialog(target) : void runEnhance(target))}
      >
        {busy ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
        ) : (
          <Sparkles className="h-3.5 w-3.5" aria-hidden />
        )}
        {empty ? 'Generate' : 'Enhance'}
      </Button>
    );
  };

  const scrollToFirstError = () => {
    const errors = form.formState.errors;

    // Function to get the first leaf error path
    const getFirstErrorPath = (obj: FieldErrors<BusinessFormValues>): string => {
      const keys = Object.keys(obj);
      if (keys.length === 0) return '';

      const firstKey = keys[0];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const value = (obj as Record<string, any>)[firstKey];

      // If it has a message, it's a leaf error node in React Hook Form
      if (value && value.message) {
        return firstKey;
      }

      // If it's an object without a message, it's a nested error group
      if (typeof value === 'object' && value !== null) {
        const subPath = getFirstErrorPath(value as FieldErrors<BusinessFormValues>);
        if (subPath) return `${firstKey}.${subPath}`;
      }

      return firstKey;
    };

    const firstErrorPath = getFirstErrorPath(errors);
    if (!firstErrorPath) return;

    const fieldToAccordion: Record<string, string> = {
      name: 'basic',
      category: 'basic',
      phone: 'basic',
      email: 'basic',
      logo: 'basic',
      addressLine1: 'addressJson',
      addressLine2: 'addressJson',
      landmark: 'addressJson',
      taluka: 'addressJson',
      district: 'addressJson',
      state: 'addressJson',
      country: 'addressJson',
      pinCode: 'addressJson',
      googleMapLink: 'addressJson',
      website: 'online',
      websiteInfo: 'online',
      description: 'details',
      speciality: 'details',
      workArea: 'details',
      keywords: 'details',
      priority: 'basic',
    };

    // Determine accordion value from the path
    let accordionValue = 'basic';
    const parts = firstErrorPath.split('.');
    // Check nested parts first for specific mapping, then fall back
    for (const part of parts) {
      if (fieldToAccordion[part]) {
        accordionValue = fieldToAccordion[part];
        break;
      }
    }

    // Open the accordion containing the error
    setOpenAccordions((prev) => (prev.includes(accordionValue) ? prev : [...prev, accordionValue]));

    // Wait for React to re-render and then scroll to the error
    const scrollToError = () => {
      const container = formContainerRef.current;
      if (!container) return;

      // Try to find the specific input field by name attribute
      const inputElement = container.querySelector(`[name="${firstErrorPath}"]`) as HTMLElement;
      if (inputElement) {
        inputElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Small delay for focus to work after scroll initiation
        setTimeout(() => inputElement.focus(), 100);
        return;
      }

      // Fallback: try to find the error message text
      const errorElements = container.querySelectorAll('.text-destructive');
      if (errorElements.length > 0) {
        (errorElements[0] as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    };

    // Use requestAnimationFrame for better timing with React re-renders
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setTimeout(scrollToError, 100);
      });
    });
  };

  const handleCardUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setIsExtracting(true);
    try {
      const prepared = await prepareImageFile(file);
      const formData = new FormData();
      formData.append('file', prepared);
      const response = await axiosInstance.post('/api/user/business/extract', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const data = response.data;
      if (data.name) form.setValue('name', data.name);
      if (data.category) {
        const parsed = parseBusinessCategory(data.category);
        if (parsed.businessCategory) form.setValue('businessCategory', parsed.businessCategory);
        form.setValue('subCategory', parsed.subCategory || data.category);
      }
      if (data.email) form.setValue('email', data.email);
      if (data.phone) {
        const phone = data.phone.startsWith('+') ? data.phone : `+${data.phone}`;
        form.setValue('phone', phone);
      }

      // Handle structured address from extraction
      if (data.addressJson) {
        if (typeof data.addressJson === 'object') {
          form.setValue('addressJson', data.addressJson);
        } else {
          form.setValue('addressJson.addressLine1', data.addressJson);
        }
      }

      // Handle keywords from extraction
      if (data.keywords) {
        form.setValue('keywords', data.keywords);
      }

      // Handle description from extraction
      if (data.description) {
        form.setValue('description', data.description);
      }

      // Handle website from extraction
      if (data.website) {
        form.setValue('website', data.website);
      }

      // Handle website info from extraction
      if (data.websiteInfo) {
        form.setValue('websiteInfo', data.websiteInfo);
      }

      // Handle work area from extraction
      if (data.workArea) {
        // Validate enum value before setting
        const validWorkAreas = ['Global', 'National Level', 'State Level', 'Local'];
        if (validWorkAreas.includes(data.workArea)) {
          form.setValue('workArea', data.workArea);
        }
      }

      setIsExtracting(false);
      setOpenAccordions((prev) => (prev.includes('basic') ? prev : ['basic', ...prev]));
      toast.success('Business details extracted from card! Review the form below.');
      requestAnimationFrame(() => {
        formContainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Extraction error:', error.response?.data || error.message);
      } else {
        console.error('Extraction error:', error);
      }
      toast.error('Failed to extract business details');
      setIsExtracting(false);
    }
  };

  const handleGenerateKeywords = async () => {
    const name = form.getValues('name');
    const businessCategory = form.getValues('businessCategory');
    const subCategory = form.getValues('subCategory');
    const category =
      businessCategory && subCategory
        ? composeBusinessCategory(businessCategory, subCategory)
        : subCategory || businessCategory;
    const description = form.getValues('description');

    if (!name) {
      toast.error('Please enter a business name first');
      return;
    }

    setIsGeneratingKeywords(true);
    try {
      const response = await axiosInstance.post('/api/user/business/generate-keywords', {
        name,
        category,
        description,
      });

      if (response.data.keywords) {
        form.setValue('keywords', response.data.keywords);
        toast.success('Keywords generated!');
      }
    } catch (error) {
      console.error('Keyword generation error:', error);
      toast.error('Failed to generate keywords');
    } finally {
      setIsGeneratingKeywords(false);
    }
  };

  const onSubmit = async (data: BusinessFormValues) => {
    setIsLoading(true);
    try {
      // Trim and remove trailing commas from address fields
      const trimAndClean = (value: string | undefined) => {
        if (!value) return undefined;
        return value.trim().replace(/,+\s*$/, '');
      };

      // Rebuild address object with only non-empty fields
      const cleanedAddress: Record<string, string> = {};
      const fieldOrder = [
        'addressLine1',
        'addressLine2',
        'landmark',
        'taluka',
        'district',
        'state',
        'country',
        'pinCode',
        'googleMapLink',
      ];
      const values = [
        trimAndClean(data.addressJson.addressLine1),
        trimAndClean(data.addressJson.addressLine2),
        trimAndClean(data.addressJson.landmark),
        trimAndClean(data.addressJson.taluka),
        trimAndClean(data.addressJson.district),
        trimAndClean(data.addressJson.state),
        trimAndClean(data.addressJson.country),
        trimAndClean(data.addressJson.pinCode),
        trimAndClean(data.addressJson.googleMapLink),
      ];

      fieldOrder.forEach((field, index) => {
        if (values[index]) {
          cleanedAddress[field] = values[index];
        }
      });

      const base = {
        name: data.name,
        businessCategory: data.businessCategory,
        subCategory: data.subCategory,
        selfDefinedCategory: data.selfDefinedCategory?.trim() || '',
        description: data.description,
        speciality: data.speciality,
        addressJson: cleanedAddress,
        phone: data.phone,
        email: data.email,
        logo: data.logo,
        website: data.website,
        websiteInfo: data.websiteInfo,
        keywords: data.keywords,
        workArea: data.workArea,
        priority: data.priority,
        socialLinks: data.socialLinks,
      };

      if (initialData?.id) {
        await axiosInstance.patch('/api/user/business', { ...base, id: data.id });
      } else {
        const res = await axiosInstance.post<Business>('/api/user/business', {
          ...base,
          ...(linkToCommunityId ? { communityId: linkToCommunityId } : {}),
        });
        await mutate();
        await globalMutate(`/api/public/business/${res.data.id}`);
        await globalMutate((key) => {
          if (typeof key !== 'string') return false;
          return key.includes('/api/user/business/') && key.includes('/community-links');
        });
        toast.success('Business profile created successfully');
        setAllowLeave(true);
        onSuccess?.({ id: res.data.id });
        return;
      }
      await mutate();
      if (initialData?.id) {
        await globalMutate(`/api/public/business/${initialData.id}`);
      }
      await globalMutate((key) => {
        if (typeof key !== 'string') return false;
        return key.includes('/api/user/business/') && key.includes('/community-links');
      });
      toast.success('Business profile updated successfully');
      setAllowLeave(true);
      onSuccess?.({ id: initialData?.id ?? data.id });
    } catch (error) {
      console.error(error);
      Sentry.captureException(error, {
        tags: {
          feature: 'business-form',
          action: initialData?.id ? 'update' : 'create',
        },
        extra: {
          businessId: initialData?.id ?? data.id,
          linkToCommunityId,
        },
      });
      toast.error(
        initialData?.id ? 'Failed to update business profile' : 'Failed to create business profile'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const isValid = await form.trigger();
    if (!isValid) {
      scrollToFirstError();
      return;
    }
    await onSubmit(form.getValues());
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="rounded-2xl border bg-background shadow-sm overflow-hidden">
        <div className="p-6 pb-4 border-b border-border/60">
          <div className="flex items-center gap-2 text-xl font-display font-bold tracking-tight">
            <Building2 className="h-5 w-5 text-primary shrink-0" />
            <div>{initialData ? 'Edit Business Profile' : 'Add Business Profile'}</div>
          </div>
          <p className="text-sm text-muted-foreground mt-2 max-w-xl leading-relaxed">
            {initialData
              ? 'Update your business details visible to community members.'
              : 'List your business so members can find you in the directory. Fill in the sections below, then save at the bottom.'}
          </p>
          {!initialData && (
            <ol className="mt-4 space-y-1.5 text-sm text-muted-foreground list-none p-0 m-0">
              <li className="flex items-start gap-2">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[11px] font-bold text-primary">
                  1
                </span>
                <span>
                  <span className="font-medium text-foreground">Basic information</span> — name,
                  category, and logo
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[11px] font-bold text-primary">
                  2
                </span>
                <span>
                  <span className="font-medium text-foreground">Contact & details</span> — phone,
                  address, and description
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[11px] font-bold text-primary">
                  3
                </span>
                <span>
                  <span className="font-medium text-foreground">Save</span> — tap Create Business
                  Profile when you are done
                </span>
              </li>
            </ol>
          )}
          {watchedBusinessCategory && watchedSubCategory && (
            <p className="text-xs text-muted-foreground mt-3">
              {formatBusinessCategoryLabel(
                watchedBusinessCategory,
                watchedSubCategory,
                watchedSelfDefinedCategory
              )}
            </p>
          )}
        </div>

        {showDuplicatePopup && (
          <div className="px-6 pb-4">
            <div className="rounded-lg border bg-muted/30 p-4">
              <div className="flex items-center gap-2 font-semibold">
                <TriangleAlert className="h-5 w-5 text-amber-500" />
                Similar Business Found
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                We found businesses similar to "{businessName}". Is one of these the business you're
                trying to create?
              </p>
              <div className="space-y-3 py-4">
                {similarBusinesses.map((business) => (
                  <div
                    key={business.id}
                    className="flex items-center justify-between p-3 rounded-lg border bg-background"
                  >
                    <div className="flex items-center gap-3">
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={business.logo || undefined} />
                        <AvatarFallback>{business.name[0]}</AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium text-sm">{business.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {formatBusinessCategoryLabel(
                            business.category,
                            business.subCategory,
                            business.selfDefinedCategory
                          )}
                        </p>
                      </div>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => handleJoin(business.id)}>
                      Join Instead
                    </Button>
                  </div>
                ))}
              </div>
              <div className="flex justify-end">
                <Button variant="secondary" onClick={() => setShowDuplicatePopup(false)}>
                  No, Create New
                </Button>
              </div>
            </div>
          </div>
        )}

        <Form {...form}>
          <form onSubmit={handleFormSubmit} className="flex flex-col">
            {!initialData && (
              <div className="px-6 pt-4">
                <div className="rounded-xl border-2 border-dashed border-border bg-white p-4 sm:p-5 dark:bg-black">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10">
                      {isExtracting ? (
                        <Loader2 className="h-6 w-6 animate-spin text-primary" />
                      ) : (
                        <Camera className="h-6 w-6 text-primary" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-semibold text-foreground">Have a business card?</p>
                        <Badge variant="outline" className="text-[10px] uppercase tracking-wide">
                          Optional
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        Upload a photo to auto-fill the form below. You can still edit everything
                        before saving.
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      disabled={isExtracting}
                      onClick={() => document.getElementById('card-upload')?.click()}
                      className="w-full sm:w-auto shrink-0 gap-2"
                    >
                      {isExtracting ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Extracting…
                        </>
                      ) : (
                        <>
                          <Upload className="h-4 w-4" />
                          Upload card photo
                        </>
                      )}
                    </Button>
                    <input
                      id="card-upload"
                      type="file"
                      className="hidden"
                      accept="image/*"
                      onChange={handleCardUpload}
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="px-6 py-2" ref={formContainerRef}>
              {!initialData && (
                <p className="text-sm font-medium text-foreground pt-2 pb-3">Business details</p>
              )}
              <Accordion
                type="multiple"
                className="w-full pt-2"
                value={openAccordions}
                onValueChange={setOpenAccordions}
              >
                <AccordionItem value="basic">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-3">
                      <div className="bg-primary/10 p-2 rounded-full">
                        <Building2 className="h-4 w-4 text-primary" />
                      </div>
                      <span className="font-medium">Basic Information</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-4 pt-4">
                      <FormField
                        control={form.control}
                        name="logo"
                        render={({ field }) => (
                          <FormItem className="flex justify-center mb-4">
                            <FormControl>
                              <ImageUpload
                                value={field.value}
                                onChange={field.onChange}
                                disabled={isLoading}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="name"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="flex items-center gap-2">
                              <Building2 className="h-4 w-4 text-muted-foreground" />
                              Business Name <span className="text-red-500">*</span>
                            </FormLabel>
                            <FormControl>
                              <Input placeholder="e.g., Acme Corporation" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="businessCategory"
                        render={() => (
                          <FormItem>
                            <FormLabel className="flex items-center gap-2">
                              <Tag className="h-4 w-4 text-muted-foreground" />
                              Category <span className="text-red-500">*</span>
                            </FormLabel>
                            <FormControl>
                              <BusinessCategoryPicker
                                businessCategory={watchedBusinessCategory}
                                subCategory={watchedSubCategory}
                                onSelect={(value) => {
                                  if (value) {
                                    form.setValue('businessCategory', value.businessCategory, {
                                      shouldValidate: true,
                                    });
                                    form.setValue('subCategory', value.subCategory, {
                                      shouldValidate: true,
                                    });
                                  } else {
                                    form.setValue('businessCategory', '', { shouldValidate: true });
                                    form.setValue('subCategory', '', { shouldValidate: true });
                                  }
                                }}
                                placeholder="Search and select a category"
                              />
                            </FormControl>
                            {(form.formState.errors.businessCategory ||
                              form.formState.errors.subCategory) && (
                              <p className="text-sm font-medium text-destructive">
                                {form.formState.errors.businessCategory?.message ||
                                  form.formState.errors.subCategory?.message}
                              </p>
                            )}
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="selfDefinedCategory"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="flex items-center gap-2">
                              <Tag className="h-4 w-4 text-muted-foreground" />
                              Self-Defined Category
                            </FormLabel>
                            <FormControl>
                              <Input placeholder="e.g., Premium Wedding Photography" {...field} />
                            </FormControl>
                            <p className="text-xs text-muted-foreground">
                              Optional. Shown on your profile and in the directory when set.
                            </p>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="priority"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="flex items-center gap-2">
                              <Layers className="h-4 w-4 text-muted-foreground" />
                              Your role among your businesses
                            </FormLabel>
                            <Select onValueChange={field.onChange} value={field.value || 'OTHER'}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select priority" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="PRIMARY">Primary business</SelectItem>
                                <SelectItem value="SECONDARY">Secondary business</SelectItem>
                                <SelectItem value="OTHER">Other</SelectItem>
                              </SelectContent>
                            </Select>
                            <p className="text-xs text-muted-foreground">
                              Used when resolving ties (e.g. curated community directory).
                            </p>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="phone"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="flex items-center gap-2">
                                <Phone className="h-4 w-4 text-muted-foreground" />
                                Phone <span className="text-red-500">*</span>
                              </FormLabel>
                              <FormControl>
                                <PhoneInput placeholder="+91 98765 43210" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="email"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="flex items-center gap-2">
                                <Mail className="h-4 w-4 text-muted-foreground" />
                                Email <span className="text-red-500">*</span>
                              </FormLabel>
                              <FormControl>
                                <Input placeholder="contact@business.com" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="addressJson">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-3">
                      <div className="bg-primary/10 p-2 rounded-full">
                        <MapPin className="h-4 w-4 text-primary" />
                      </div>
                      <span className="font-medium">Business Address</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-4 pt-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="addressJson.addressLine1"
                          render={({ field }) => (
                            <FormItem className="sm:col-span-2">
                              <FormLabel className="flex items-center gap-2">
                                <MapPin className="h-4 w-4 text-muted-foreground" />
                                Street Address <span className="text-red-500">*</span>
                              </FormLabel>
                              <FormControl>
                                <Input placeholder="123 Main Street, Building Name" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="addressJson.addressLine2"
                          render={({ field }) => (
                            <FormItem className="sm:col-span-2">
                              <FormLabel className="flex items-center gap-2">
                                <MapPin className="h-4 w-4 text-muted-foreground" />
                                Address Line 2 (Optional)
                              </FormLabel>
                              <FormControl>
                                <Input placeholder="Apartment, suite, floor, etc." {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="addressJson.landmark"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="flex items-center gap-2">
                                <Layers className="h-4 w-4 text-muted-foreground" />
                                Landmark (Optional)
                              </FormLabel>
                              <FormControl>
                                <Input placeholder="Near City Mall" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="addressJson.pinCode"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="flex items-center gap-2">
                                <MapPin className="h-4 w-4 text-muted-foreground" />
                                Pin Code <span className="text-red-500">*</span>
                              </FormLabel>
                              <div className="flex gap-2">
                                <FormControl className="flex-1">
                                  <Input
                                    placeholder="Enter pin code for auto-fill"
                                    {...field}
                                    onChange={(e) => {
                                      field.onChange(e);
                                      if (e.target.value.length >= 5) {
                                        handlePincodeChange(e.target.value);
                                      }
                                    }}
                                  />
                                </FormControl>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="icon"
                                  onClick={() => handlePincodeChange(field.value)}
                                  disabled={pincodeLoading || (field.value ?? '').length < 5}
                                  title="Look up address details"
                                  className="shrink-0"
                                >
                                  {pincodeLoading ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <Search className="h-4 w-4" />
                                  )}
                                </Button>
                              </div>
                              <p className="text-xs text-muted-foreground">
                                Enter 5+ digit pin code to auto-fill address
                              </p>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="addressJson.googleMapLink"
                          render={({ field }) => (
                            <FormItem className="sm:col-span-2">
                              <FormLabel className="flex items-center gap-2">
                                <MapPin className="h-4 w-4 text-muted-foreground" />
                                Google Maps Link (Optional)
                              </FormLabel>
                              <FormControl>
                                <Input placeholder="https://maps.google.com/..." {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="addressJson.country"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="flex items-center gap-2">
                                <MapPin className="h-4 w-4 text-muted-foreground" />
                                Country <span className="text-red-500">*</span>
                              </FormLabel>
                              <Select onValueChange={field.onChange} value={field.value}>
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select country" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="India">🇮🇳 India</SelectItem>
                                  <SelectItem value="United States">🇺🇸 United States</SelectItem>
                                  <SelectItem value="United Kingdom">🇬🇧 United Kingdom</SelectItem>
                                  <SelectItem value="Canada">🇨🇦 Canada</SelectItem>
                                  <SelectItem value="Australia">🇦🇺 Australia</SelectItem>
                                  <SelectItem value="Germany">🇩🇪 Germany</SelectItem>
                                  <SelectItem value="France">🇫🇷 France</SelectItem>
                                  <SelectItem value="UAE">🇦🇪 United Arab Emirates</SelectItem>
                                  <SelectItem value="Singapore">🇸🇬 Singapore</SelectItem>
                                  <SelectItem value="Other">🌍 Other</SelectItem>
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="addressJson.state"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="flex items-center gap-2">
                                <MapPin className="h-4 w-4 text-muted-foreground" />
                                State <span className="text-red-500">*</span>
                              </FormLabel>
                              {(addressOptions.states ?? []).length > 0 ? (
                                <Select onValueChange={field.onChange} value={field.value}>
                                  <FormControl>
                                    <SelectTrigger>
                                      <SelectValue placeholder="Select state" />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent>
                                    {(addressOptions.states ?? []).map((state) => (
                                      <SelectItem key={state} value={state}>
                                        {state}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              ) : (
                                <Input placeholder="Enter state" {...field} />
                              )}
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="addressJson.district"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="flex items-center gap-2">
                                <MapPin className="h-4 w-4 text-muted-foreground" />
                                District
                              </FormLabel>
                              {(addressOptions.districts ?? []).length > 0 ? (
                                <Select onValueChange={field.onChange} value={field.value}>
                                  <FormControl>
                                    <SelectTrigger>
                                      <SelectValue placeholder="Select district" />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent>
                                    {(addressOptions.districts ?? []).map((district) => (
                                      <SelectItem key={district} value={district}>
                                        {district}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              ) : (
                                <Input placeholder="Enter district" {...field} />
                              )}
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="addressJson.taluka"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="flex items-center gap-2">
                                <MapPin className="h-4 w-4 text-muted-foreground" />
                                Taluka / Tehsil
                              </FormLabel>
                              {(addressOptions.talukas ?? []).length > 0 ? (
                                <Select onValueChange={field.onChange} value={field.value}>
                                  <FormControl>
                                    <SelectTrigger>
                                      <SelectValue placeholder="Select taluka" />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent>
                                    {(addressOptions.talukas ?? []).map((taluka) => (
                                      <SelectItem key={taluka} value={taluka}>
                                        {taluka}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              ) : (
                                <Input placeholder="Enter taluka" {...field} />
                              )}
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="online">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-3">
                      <div className="bg-primary/10 p-2 rounded-full">
                        <Globe className="h-4 w-4 text-primary" />
                      </div>
                      <span className="font-medium">Online Presence</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-4 pt-4">
                      <FormField
                        control={form.control}
                        name="website"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="flex items-center gap-2">
                              <Link2 className="h-4 w-4 text-muted-foreground" />
                              Website URL
                            </FormLabel>
                            <FormControl>
                              <Input placeholder="https://example.com" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="websiteInfo"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="flex items-center gap-2">
                              <Globe className="h-4 w-4 text-muted-foreground" />
                              Website Display Text
                            </FormLabel>
                            <FormControl>
                              <Input placeholder="Click here to view our catalogue" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="socialLinks"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="flex items-center gap-2">
                              <Share2 className="h-4 w-4 text-muted-foreground" />
                              Social Media Links
                            </FormLabel>
                            <FormControl>
                              <SocialLinksEditor
                                value={field.value ?? null}
                                onChange={(val) => field.onChange(val)}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="details">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-3">
                      <div className="bg-primary/10 p-2 rounded-full">
                        <FileText className="h-4 w-4 text-primary" />
                      </div>
                      <span className="font-medium">Business Details</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-4 pt-4">
                      <FormRichTextField
                        control={form.control}
                        name="description"
                        label="About us"
                        description="Introduce your business, products, or services. Formatting is preserved when others view your profile."
                        labelActions={(field) =>
                          renderRichAssistActions(
                            'description',
                            field as ControllerRenderProps<BusinessFormValues, 'description'>
                          )
                        }
                      />
                      <FormRichTextField
                        control={form.control}
                        name="speciality"
                        label="Speciality"
                        description="Highlight what your business is especially known for."
                        labelActions={(field) =>
                          renderRichAssistActions(
                            'speciality',
                            field as ControllerRenderProps<BusinessFormValues, 'speciality'>
                          )
                        }
                      />
                      <FormField
                        control={form.control}
                        name="workArea"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="flex items-center gap-2">
                              <Workflow className="h-4 w-4 text-muted-foreground" />
                              Work Area (Business Territory)
                            </FormLabel>
                            <Select onValueChange={field.onChange} value={field.value || undefined}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select your service area" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="Global">
                                  <div className="flex items-center gap-2">
                                    <Globe className="h-4 w-4" />
                                    Global
                                  </div>
                                </SelectItem>
                                <SelectItem value="National Level">
                                  <div className="flex items-center gap-2">
                                    <MapPin className="h-4 w-4" />
                                    National Level
                                  </div>
                                </SelectItem>
                                <SelectItem value="State Level">
                                  <div className="flex items-center gap-2">
                                    <MapPin className="h-4 w-4" />
                                    State Level
                                  </div>
                                </SelectItem>
                                <SelectItem value="Local">
                                  <div className="flex items-center gap-2">
                                    <MapPin className="h-4 w-4" />
                                    Local
                                  </div>
                                </SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="keywords"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="flex items-center gap-2">
                              <Tag className="h-4 w-4 text-muted-foreground" />
                              Keywords
                              <Badge variant="secondary" className="text-xs">
                                For search optimization
                              </Badge>
                            </FormLabel>
                            <div className="flex gap-2">
                              <FormControl className="flex-1">
                                <Input
                                  placeholder="e.g., plumbing, home repair, emergency services"
                                  {...field}
                                />
                              </FormControl>
                              <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                onClick={handleGenerateKeywords}
                                disabled={isGeneratingKeywords}
                                title="Auto-generate keywords from business info"
                                className="shrink-0"
                              >
                                {isGeneratingKeywords ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Sparkles className="h-4 w-4" />
                                )}
                              </Button>
                            </div>
                            <p className="text-xs text-muted-foreground">
                              Use commas to separate keywords
                            </p>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>

            <div className="p-6 pt-4 border-t mt-0 bg-background sticky bottom-0 z-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              {!initialData && (
                <p className="text-xs text-muted-foreground">
                  Review your details, then save to add your business to the directory.
                </p>
              )}
              <Button
                type="submit"
                disabled={isLoading || isExtracting}
                className="w-full sm:w-auto sm:ml-auto gap-2 rounded-xl font-semibold shadow-primary/20"
              >
                {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                <Building2 className="h-4 w-4" />
                {initialData ? 'Update Business Profile' : 'Create Business Profile'}
              </Button>
            </div>
          </form>
        </Form>
      </div>
      <Dialog
        open={refineTarget !== null}
        onOpenChange={(open) => {
          if (!open) closeRefineDialog();
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {refineTarget === 'description' ? 'Generate About us' : 'Generate Speciality'}
            </DialogTitle>
            <DialogDescription>
              In a few words, tell us{' '}
              {refineTarget === 'description'
                ? 'about your business—what you do and who you serve.'
                : 'what you specialise in or what sets you apart.'}{' '}
              We will turn it into polished, formatted text you can still edit.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={refineDraft}
            onChange={(e) => setRefineDraft(e.target.value)}
            placeholder="e.g. Family-run bakery since 1998, sourdough and custom cakes…"
            rows={4}
            className="resize-y min-h-[100px]"
          />
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={closeRefineDialog}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => void submitGenerateFromDialog()}
              disabled={!!refiningField || refineDraft.trim().length < 3}
              className="gap-2"
            >
              {refiningField && refineTarget !== null && refiningField === refineTarget ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              ) : (
                <Sparkles className="h-4 w-4" aria-hidden />
              )}
              Refine for me
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {unsavedChangesDialog}
    </div>
  );
}
